import { QueryHookOptions, gql } from '@apollo/client'
import { usePersistedCacheQuery } from 'app/hooks'
import { Comment } from 'app/types'

const query = gql`
  fragment comment on Comment {
    id
    content
    editedAt
    createdAt
    content
    isDeleted
    parentComment {
      id
    }
    replyingTo {
      id
      fullName
    }
    creator {
      id
      fullName
      university {
        id
        name
      }
      subjectOfStudy {
        id
        name
      }
      yearOfStudy
      avatar {
        id
        sizes {
          small {
            url
          }
          medium {
            url
          }
        }
      }
    }
  }

  fragment childCommentFragment on Comment {
    ...comment
    replies {
      ...comment
      replies {
        ...comment
      }
    }
  }

  fragment commentFragment on Comment {
    ...comment
    replies {
      ...childCommentFragment
    }
  }

  query getPostComments($id: String!, $comment: String, $page: Int, $limit: Int) {
    groupPostComments(
      id: $id
      comment: $comment
      page: $page
      limit: $limit
    ) {
      totalDocs
      page
      totalPages
      docs {
        ...commentFragment
      }
    }
  }
`

export interface CommentFragment {
  id: string
  content: string
  editedAt: string
  createdAt: string
  parentComment?: {
    id: string
  }
  replyingTo?: {
    id: string
    fullName: string
  }
  isDeleted: boolean
  creator: {
    id: string
    fullName: string
    university: {
      id: string
      name: string
    }
    subjectOfStudy: {
      id: string
      name: string
    }
    yearOfStudy: string
    avatar: {
      id: string
      sizes: {
        small: {
          url: string
        }
        medium: {
          url: string
        }
      }
    }
  }
  replies: CommentFragment[]
}

export interface PostCommentsQueryInput {
  id: string
  comment?: string
  page: number
  limit: number
}

export interface PostCommentsQueryOutput {
  groupPostComments: {
    totalDocs: number
    page: number
    totalPages: number
    docs: CommentFragment[]
  }
}

const mapSingleComment = (comment: CommentFragment): Comment => {
  const creator = comment.creator
  return {
    id: comment.id,
    content: comment.content,
    replies: (comment?.replies || []).map(mapSingleComment),
    creator: {
      id: creator.id,
      fullName: creator.fullName,
      avatar: {
        small: creator.avatar?.sizes.small.url,
        medium: creator.avatar?.sizes.medium.url,
      },
      yearOfStudy: creator.yearOfStudy,
      subjectOfStudy: {
        id: creator.subjectOfStudy?.id,
        name: creator.subjectOfStudy?.name,
      },
      university: {
        id: creator.university?.id,
        name: creator.university?.name,
      },
    },
    editedAt: comment.editedAt,
    createdAt: comment.createdAt,
    isDeleted: comment.isDeleted,
    parentComment: comment.parentComment?.id,
    ...(comment.replyingTo && {
      replyingTo: {
        id: comment.replyingTo.id,
        fullName: comment.replyingTo.fullName,
      },
    }),
  }
}

export const createNestedComments = (
  comments: Comment[],
  parentComment: string | undefined = undefined
): Comment[] => {
  const nestedComments: Comment[] = []

  for (const comment of comments) {
    if (comment.parentComment === parentComment) {
      const replies = createNestedComments(comments, comment.id)
      if (replies?.length) {
        comment.replies = replies
      }
      nestedComments.push(comment)
    }
  }

  return nestedComments
}

export interface PostCommentsMapFnOutput {
  totalDocs: number
  page: number
  totalPages: number
  docs: Comment[]
}

const mapFn = (data: PostCommentsQueryOutput['groupPostComments']['docs'] | undefined): Comment[] => {
  const comments = data || []
  const nestedComment = createNestedComments(comments.map(mapSingleComment))
  return nestedComment
}

export const useGroupPostCommentsQuery = (
  input: PostCommentsQueryInput,
  options?: QueryHookOptions<PostCommentsQueryOutput, PostCommentsQueryInput>
) =>
  usePersistedCacheQuery<PostCommentsQueryOutput, PostCommentsQueryInput>(query, {
    variables: input,
    ...options,
  })

export const groupPostCommentsQuery = {
  query,
  mapFn,
}
